import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import Image from 'next/image';

import { useInnerWidth } from 'hooks';
import { isImageExist } from 'utils';
import { COMPANY_LOGOS_TO_INVERT } from 'utils/constant';

import GenericCompanyLogo from './genericLogo';
import CompanyLogoSkeleton from './logoSkeleton';

import styles from './companyLogo.module.css';

type LogoSize =
  | 'xxsmall'
  | 'xsmall'
  | 'small'
  | 'xmedium'
  | 'medium'
  | 'xlarge'
  | 'xxlarge'
  | 'large'
  | 'review-card'
  | '';

interface CompanyLogoProps {
  companyLogo?: string;
  companyName: string;
  size?: LogoSize;
  imgWidth?: number;
  imgHeight?: number;
  objectFit?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down';
  className?: string;
}

const CompanyLogo = (props: CompanyLogoProps) => {
  const {
    companyLogo = null,
    companyName = '',
    className = '',
    size = '',
    imgWidth,
    imgHeight,
    objectFit = 'contain',
  } = props;

  const [isError, setIsError] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const width = useInnerWidth();
  const isInverted = COMPANY_LOGOS_TO_INVERT.includes(companyName);

  useEffect(() => {
    let isMounted = true;

    const check = async () => {
      const { status } = await isImageExist(companyLogo);
      if (isMounted) {
        setIsError(status === 'error');
        setIsChecking(false);
      }
    };
    if (companyLogo) {
      check();
    } else {
      setIsChecking(false);
      setIsError(true);
    }

    return () => {
      isMounted = false;
    };
  }, [companyLogo]);

  const getSize = (): number | '100%' => {
    switch (size) {
      case 'xxsmall':
        return 30;

      case 'xsmall':
        return 40;

      case 'small':
        return 50;

      case 'xmedium':
        return 55;

      case 'medium':
        return 70;

      case 'xlarge':
        return 100;

      case 'xxlarge':
        return 120;

      case 'large':
        return width <= 991 ? 80 : 140;

      case 'review-card':
        return 60;

      default:
        return '100%';
    }
  };

  if (!isChecking) {
    if (!isError && companyLogo) {
      return (
        <div
          className={classNames(
            styles['generic-company-logo'],
            'generic-company-logo',
            styles[size],
            className,
            'd-flex',
            'align-items-center',
            'justify-content-center',
            {
              [styles['inverted']]: isInverted,
            }
          )}
        >
          <Image
            src={companyLogo}
            loader={() => companyLogo}
            alt={`${companyName || 'Company'} logo`}
            width={getSize()}
            height={getSize()}
            objectFit={objectFit}
          />
        </div>
      );
    }
    return (
      <GenericCompanyLogo
        className={className}
        size={size}
        companyName={companyName}
        imgWidth={imgWidth}
        imgHeight={imgHeight}
      />
    );
  }

  return <CompanyLogoSkeleton className={className} size={size} />;
};

export default CompanyLogo;
export type { LogoSize };
